/**
 * actionList.ts — Pinia Store
 *
 * Morning Report & Action List, 100% client-side.
 *
 * Storage: `mycrogym_studio_config` IndexedDB → `morning_report` table
 *   key:     'report_<companyid>_<userid>'
 *   options: MorningReportRecord (full serialised report)
 *
 * No server API calls. All source data (bookings, events, resources)
 * comes from the already-synced main `mycrogym_studio` Dexie DB.
 */

import { defineStore }         from 'pinia'
import { ref, computed }       from 'vue'
import Dexie                   from 'dexie'
import { openConfigDb }        from '@/core/worker/db-constants.js'
import { useTablesStore }      from '@/stores/tables'
import { ActionRulesEngine }   from '@/core/services/ActionRulesEngine'
import type { SyncChange }     from '@/core/services/ActionRulesEngine'
import type { ActionOptions }  from '@/core/services/ActionRulesEngine'

// ── Types ─────────────────────────────────────────────────────────────────────

export interface TopicConfig {
  id:      string   // matches ActionItem.type
  label:   string
  icon:    string
  enabled: boolean
  order:   number   // 1-based display order
}

export interface MorgenSettings {
  topics: TopicConfig[]
  ttsRate:   number  // 0.5 – 2.0, default 1.0
  ttsVolume: number  // 0.0 – 1.0, default 1.0
}

export const DEFAULT_TOPICS: TopicConfig[] = [
  { id: 'sick_report',       label: 'Krankmeldungen',         icon: '🤒', enabled: true, order: 1 },
  { id: 'coverage_gap',      label: 'Trainerausfall',         icon: '🚨', enabled: true, order: 2 },
  { id: 'empty_event',       label: 'Kurse ohne Teilnehmer',  icon: '⚠️', enabled: true, order: 3 },
  { id: 'booking_cancelled', label: 'Stornierungen',          icon: '❌', enabled: true, order: 4 },
  { id: 'email',             label: 'Mailanfragen',           icon: '📬', enabled: true, order: 5 },
  { id: 'holiday_request',   label: 'Urlaubsanfragen',        icon: '🏖️', enabled: true, order: 6 },
]

export interface ActionItem {
  id:        string
  type:      string        // booking_cancelled | empty_event | sick_report | email | …
  options:   ActionOptions
  createdAt: string        // ISO string — when generated
}

export interface MorningReportRecord {
  key:         string         // IDB primary key
  companyid:   string
  userid:      string
  generatedAt: string         // ISO — when the report was last generated
  lastSeenAt:  string | null  // ISO — when user last dismissed the panel
  items:       ActionItem[]   // all generated/classified items
}

// ── Store ─────────────────────────────────────────────────────────────────────

export const useActionListStore = defineStore('actionList', () => {

  // ── State ──────────────────────────────────────────────────────────────────
  const items       = ref<ActionItem[]>([])
  const isVisible   = ref(false)
  const isAnalysing = ref(false)
  let classifierWorker: Worker | null = null

  // ── IDB helpers ────────────────────────────────────────────────────────────
  let _configDb: Dexie | null = null

  async function getConfigDb (): Promise<Dexie> {
    if (_configDb) return _configDb
    // Version ladder comes from db-constants.js — declaring extra stores at an
    // EXISTING version number triggers Dexie's SchemaDiff auto-bump, which
    // force-closes every other connection (main thread + both workers) mid-sync.
    const db = await openConfigDb(new Dexie('mycrogym_studio_config'))
    _configDb = db
    return db
  }

  function getCompanyId (): string {
    return (useTablesStore().config as any)?.ActiveCompany || ''
  }

  function getUserId (): string {
    return (useTablesStore().config as any)?.UserId || 'default'
  }

  function getMainDb (): any {
    return (useTablesStore() as any)._db
  }

  function reportKey (): string {
    return `report_${getCompanyId()}_${getUserId()}`
  }

  // ── Persist to IDB ─────────────────────────────────────────────────────────

  /** Deep-strip Vue reactive proxies so IDB structured-clone can serialize the data */
  function deproxy<T> (val: T): T {
    return JSON.parse(JSON.stringify(val))
  }

  async function saveReport (record: Partial<MorningReportRecord>): Promise<void> {
    try {
      const db  = await getConfigDb()
      const key = reportKey()
      const existing: MorningReportRecord | undefined = await (db as any).table('morning_report').get(key)
      // Merge and strip all Vue proxies before handing to IDB
      const merged = deproxy({ ...existing, key, ...record })
      await (db as any).table('morning_report').put(merged)
    } catch (err) {
      console.warn('[ActionList] Failed to save report to IDB:', err)
    }
  }

  // ── Load report from IDB on startup ───────────────────────────────────────

  async function loadFromIDB (): Promise<void> {
    try {
      const db     = await getConfigDb()
      const key    = reportKey()
      const record: MorningReportRecord | undefined = await (db as any).table('morning_report').get(key)

      if (!record) return

      // Only show items generated since the last "dismiss" (lastSeenAt)
      const since = record.lastSeenAt ? new Date(record.lastSeenAt) : new Date(0)
      items.value = (record.items || []).filter(i =>
        (i.options?.status || 'open') === 'open' &&
        new Date(i.createdAt) > since
      )

      if (items.value.length > 0) {
        isVisible.value = true
      }
    } catch (err) {
      console.warn('[ActionList] Failed to load report from IDB:', err)
    }
  }

  // ── Settings ───────────────────────────────────────────────────────────────

  const settings = ref<MorgenSettings>({
    topics:    [...DEFAULT_TOPICS],
    ttsRate:   1.0,
    ttsVolume: 1.0
  })

  function settingsKey (): string {
    return `morgen_settings_${getUserId()}`
  }

  async function loadSettings (): Promise<void> {
    try {
      const db     = await getConfigDb()
      const record = await (db as any).table('params').get(settingsKey())
      if (!record?.value) return
      const saved: MorgenSettings = typeof record.value === 'string'
        ? JSON.parse(record.value)
        : record.value
      // Merge with defaults so newly added topics always appear
      const savedIds = new Set(saved.topics.map((t: TopicConfig) => t.id))
      const merged   = [
        ...saved.topics,
        ...DEFAULT_TOPICS.filter(t => !savedIds.has(t.id)).map((t, i) => ({
          ...t, order: saved.topics.length + i + 1
        }))
      ]
      settings.value = { ...saved, topics: merged }
    } catch (err) {
      console.warn('[ActionList] Failed to load settings:', err)
    }
  }

  async function saveSettings (patch: Partial<MorgenSettings>): Promise<void> {
    Object.assign(settings.value, patch)
    try {
      const db = await getConfigDb()
      await (db as any).table('params').put({
        param: settingsKey(),
        value: JSON.stringify(settings.value)
      })
    } catch (err) {
      console.warn('[ActionList] Failed to save settings:', err)
    }
  }



  // ── Computed ───────────────────────────────────────────────────────────────

  const openItems   = computed(() => items.value.filter(i => (i.options?.status ?? 'open') === 'open'))
  const urgentItems = computed(() => openItems.value.filter(i => i.options?.priority === 1))
  const emailItems  = computed(() => openItems.value.filter(i => i.type === 'email'))
  const groupedByPriority = computed(() => ({
    urgent: openItems.value.filter(i => i.options?.priority === 1),
    high:   openItems.value.filter(i => i.options?.priority === 2),
    normal: openItems.value.filter(i => i.options?.priority === 3),
    low:    openItems.value.filter(i => i.options?.priority === 4)
  }))

  // ── Process sync diff → add new action items ───────────────────────────────

  async function processDiff (changes: SyncChange[]): Promise<void> {
    if (!changes.length) return

    // If the main Dexie DB isn't open yet (e.g. during test simulation before first sync),
    // fall back to a minimal stub so the rules engine can still run.
    let db = getMainDb()
    if (!db) {
      // Fall back to the config DB — calendar_bookings/events tables are part of its schema
      // (always empty, but Dexie queries won't throw)
      db = await getConfigDb()
    }

    // No API needed — rules engine only writes to local state
    const engine    = new ActionRulesEngine(db)
    const generated = await engine.processDiff(changes)
    console.debug('[ActionList] processDiff generated:', generated.map(g => `${g.type}/${g.options.title}`))

    if (generated.length) {
      const newItems: ActionItem[] = generated.map(g => ({
        id:        crypto.randomUUID(),
        type:      g.type,
        options:   { ...g.options, status: 'open' } as ActionOptions,
        createdAt: new Date().toISOString()
      }))

      items.value.unshift(...newItems)
      isVisible.value = true

      // Persist merged list to IDB
      const allItems = deduplicateItems([...newItems, ...items.value])
      await saveReport({
        companyid:   getCompanyId(),
        userid:      getUserId(),
        generatedAt: new Date().toISOString(),
        items:       allItems
      })
    }
  }

  // ── Email classification ───────────────────────────────────────────────────

  function ensureWorker (): Worker {
    if (!classifierWorker) {
      classifierWorker = new Worker(
        new URL('@/core/worker/classifier.worker.ts', import.meta.url),
        { type: 'module' }
      )
    }
    return classifierWorker
  }

  function warmupClassifier (): void {
    try { ensureWorker().postMessage({ type: 'WARMUP' }) } catch {}
  }

  async function classifyEmailsWithContext (messages: Array<{
    id: string; subject: string; from: string; fromEmail?: string
    date: string; snippet: string; unread?: boolean
  }>): Promise<void> {
    const targets = messages.filter(m => m.unread !== false)
    if (!targets.length) return

    isAnalysing.value = true
    const worker = ensureWorker()

    const promise = new Promise<void>(resolve => {
      const handler = async (event: MessageEvent) => {
        if (event.data.type !== 'BATCH_RESULT') return
        worker.removeEventListener('message', handler)

        const results: Array<{ id: string; topic: string; label: string; confidence: number }> = event.data.results
        const msgMap   = new Map(targets.map(m => [m.id, m]))
        const newItems: ActionItem[] = []

        for (const r of results) {
          const msg = msgMap.get(r.id)
          if (!msg) continue

          // Skip emails with very low confidence — likely classification noise
          if (r.confidence < 0.15) continue

          // Classify as prospect if topic indicates new interest / enquiry from unknown sender
          const isProspect = ['prospect', 'anfrage', 'inquiry'].includes(r.topic?.toLowerCase() ?? '')

          newItems.push({
            id:   crypto.randomUUID(),
            type: 'email',
            options: {
              priority:     r.confidence < 0.4 ? 3 : 2,
              title:        `${r.label}: ${msg.subject}`,
              description:  `Von: ${msg.from}\n${msg.snippet}`,
              status:       'open',
              related_id:   msg.id,
              related_type: 'gmail',
              confidence:   r.confidence,
              is_prospect:  isProspect
            },
            createdAt: new Date().toISOString()
          })

        }

        if (newItems.length) {
          items.value.unshift(...newItems)
          isVisible.value = true
          const allItems = deduplicateItems([...newItems, ...items.value])
          await saveReport({
            companyid:   getCompanyId(),
            userid:      getUserId(),
            generatedAt: new Date().toISOString(),
            items:       allItems
          })
        }

        isAnalysing.value = false
        resolve()
      }
      worker.addEventListener('message', handler)
    })

    worker.postMessage({
      type:  'CLASSIFY_BATCH',
      items: targets.map(m => ({ id: m.id, text: `${m.subject}. ${m.snippet}` }))
    })

    await promise
  }

  // ── User actions (saved locally to IDB) ───────────────────────────────────

  async function updateItem (id: string, patch: Partial<ActionOptions>): Promise<void> {
    const item = items.value.find(i => i.id === id)
    if (item) Object.assign(item.options, patch)

    // Persist updated list
    await saveReport({ items: [...items.value] })
  }

  function dismissItem (id: string): Promise<void> {
    return updateItem(id, { status: 'dismissed' }).then(() => {
      items.value = items.value.filter(i => i.id !== id)
    })
  }

  function markDone (id: string): Promise<void> {
    return updateItem(id, { status: 'done' }).then(() => {
      items.value = items.value.filter(i => i.id !== id)
    })
  }

  function showReport () { isVisible.value = true }

  async function dismiss () {
    isVisible.value = false
    await saveReport({ lastSeenAt: new Date().toISOString() })
  }

  function getLastSeen (): Date {
    // Approximated for display — async load would need await
    return new Date()
  }

  // ── Helpers ────────────────────────────────────────────────────────────────

  function deduplicateItems (all: ActionItem[]): ActionItem[] {
    const seen = new Set<string>()
    return all.filter(i => {
      const key = `${i.options?.related_id}::${i.type}`
      if (seen.has(key)) return false
      seen.add(key)
      return true
    })
  }

  return {
    items, isVisible, isAnalysing,
    openItems, urgentItems, emailItems, groupedByPriority,
    settings,
    loadFromIDB, loadSettings, saveSettings,
    processDiff,
    classifyEmailsWithContext,
    updateItem, dismissItem, markDone,
    showReport, dismiss, warmupClassifier,
    getLastSeen
  }
})

